/**
 * LAS PREGUNTAS DEL QUINCENAL (doc «REPORTE QUINCENAL», 24-10).
 *
 *     PREGUNTA 1 DE 5
 *     ¿Algún problema con la dieta?
 *     [No]  [Sí]          (sin flecha desde el 26-10)
 *
 * Las preguntas, su orden y sus palabras las manda el servidor (`ficha.preguntas_quincenal`,
 * de `core/quincenal.PREGUNTAS`): sin rutina no llegan las de ejercicios y se lee «de 3».
 * Aquí solo se pintan, como en sus maquetas:
 *
 *   - EL «NO» MARCADO DE ENTRADA (`lib/revisarReporte.respuestaDelQuincenal`).
 *   - LA CASILLA CON BORDE DISCONTINUO. En la 2 y la 3 sale siempre (en su maqueta, con el
 *     no marcado); en la 1 y la 4, solo con el sí. Escribir en ella con el no marcado es
 *     contestar que sí, y se marca solo.
 *   - LA QUINTA ES LIBRE y es `notes`, con «Opcional.» debajo.
 */
import React from 'react';
import { respuestaDelQuincenal } from '../../lib/revisarReporte';
import { CabeceraDelQuincenal, CasillaLibre as Casilla, Pildora as Boton } from './piezas';

/**
 * ENVIADO (artefacto del 26-10):
 *
 *     REPORTE QUINCENAL · Enviado          (barra de tres tramos, los tres llenos)
 *     REPORTE QUINCENAL ENVIADO
 *     Te daremos respuesta el viernes antes de las 15:00, hora de España.
 *
 * La frase la manda el servidor (`ficha.frase_al_enviar`); sin plan personalizado no llega y
 * no se promete nada (21-10): queda el titular solo.
 */
export const QuincenalEnviado = ({ fraseAlEnviar }) => (
    <div className="space-y-4" data-testid="quincenal-paso3">
        <CabeceraDelQuincenal titulo="Reporte quincenal" sub="Enviado" paso={3} />
        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-center space-y-1"
            data-testid="quincenal-promesa">
            <p className="text-lg font-bold uppercase text-emerald-500"
                style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.02em' }}>
                Reporte quincenal enviado
            </p>
            {fraseAlEnviar && (
                <p className="text-sm text-foreground/80" data-testid="quincenal-frase-al-enviar">{fraseAlEnviar}</p>
            )}
        </div>
    </div>
);


const Rotulo = ({ numero, de }) => (
    <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
        Pregunta {numero} de {de}
    </p>
);

const QuincenalPreguntas = ({ preguntas, valores, set }) => {
    const respuestas = valores.respuestas_quincenal || {};
    const poner = (clave, cambio) => set('respuestas_quincenal', {
        ...respuestas, [clave]: { ...respuestaDelQuincenal(valores, clave), ...cambio } });

    return (
        <div className="grid gap-3 md:grid-cols-2" data-testid="quincenal-preguntas">
            {(preguntas || []).map((p) => {
                if (p.clave === 'notes') {
                    return (
                        <div key={p.clave} data-testid="quincenal-pregunta-notes"
                            className="rounded-2xl bg-card border border-border p-4 space-y-3">
                            <Rotulo numero={p.numero} de={p.de} />
                            <p className="text-base font-bold text-foreground">{p.pregunta}</p>
                            <Casilla valor={valores.notes || ''} onChange={(v) => set('notes', v)}
                                placeholder={p.campo} testid="quincenal-notes" />
                            {p.pie && <p className="text-[13px] text-muted-foreground">{p.pie}</p>}
                        </div>
                    );
                }
                const r = respuestaDelQuincenal(valores, p.clave);
                const conSi = r.respuesta === 'si';
                return (
                    <div key={p.clave} data-testid={`quincenal-pregunta-${p.clave}`}
                        className="rounded-2xl bg-card border border-border p-4 space-y-3">
                        <Rotulo numero={p.numero} de={p.de} />
                        <p className="text-base font-bold text-foreground">{p.pregunta}</p>
                        {p.ayuda && <p className="text-sm text-muted-foreground">{p.ayuda}</p>}
                        <div className="flex flex-wrap gap-2">
                            <Boton activo={!conSi} onClick={() => poner(p.clave, { respuesta: 'no' })}
                                testid={`quincenal-${p.clave}-no`}>No</Boton>
                            <Boton activo={conSi} onClick={() => poner(p.clave, { respuesta: 'si' })}
                                testid={`quincenal-${p.clave}-si`}>{p.si}</Boton>
                        </div>
                        {(conSi || p.campo_siempre) && (
                            <Casilla valor={r.detalle}
                                onChange={(v) => poner(p.clave, v.trim() ? { detalle: v, respuesta: 'si' } : { detalle: v })}
                                placeholder={p.campo} testid={`quincenal-${p.clave}-detalle`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default QuincenalPreguntas;
